import { defineNuxtPlugin } from 'nuxt/app'
import { ref, shallowReadonly } from 'vue'
import { LD_CLIENT, LD_FLAG } from 'launchdarkly-vue-client-sdk'
import type { useLDClient } from 'launchdarkly-vue-client-sdk'
import { ldLog } from '@/utils/dev-log/index.js'

/**
 * Import feature flags from files instead of LaunchDarkly for development
 */
async function devFeatureFlags(): Promise<Record<string, boolean | string>> {
  const devFeatureFlagsFile = await import(`@@/feature-flags-dev.json`)
  const localOverridesFile = await import(`@@/feature-flags-dev.local.json`)

  return {
    ...devFeatureFlagsFile.default,
    ...localOverridesFile.default,
  }
}

export default defineNuxtPlugin({
  name: 'launch-darkly',
  async setup(nuxt) {
    // stub client for dev
    nuxt.vueApp.provide(LD_CLIENT, {
      async waitForInitialization() {
        return Promise.resolve()
      },
      identify(...args: unknown[]) {
        ldLog({ title: 'Identify', label: 'dev', data: args })
      },
      variation<T = boolean>(flagKey: string, defaultValue?: T): T {
        const value = flags[flagKey] ?? defaultValue
        ldLog({
          title: 'FF (static)',
          label: flagKey,
          data: { value, defaultValue },
        })
        return value as T
      },
    } as ReturnType<typeof useLDClient>)

    const flags = await devFeatureFlags()

    // stub flag for dev
    nuxt.vueApp.provide(
      LD_FLAG,
      <T>(flagKey: string, defaultValue?: T): Readonly<Ref<T>> => {
        const value = flags[flagKey] ?? defaultValue

        ldLog({ title: 'FF', label: flagKey, data: { value, defaultValue } })

        return shallowReadonly(ref(value)) as Readonly<Ref<T>>
      },
    )
  },
})
